import React, { useState } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Delete } from "lucide-react";

export default function DialpadPage() {
  const [number, setNumber] = useState("");
  const [calling, setCalling] = useState(false);
  const [callModal, setCallModal] = useState(null);
  const [callResponse, setCallResponse] = useState({ response: "", notes: "" });

  const dial = (digit) => setNumber(prev => prev + digit);
  const backspace = () => setNumber(prev => prev.slice(0, -1));

  const makeCall = async () => {
    if (!number || number.length < 10) { toast.error("Enter valid number"); return; }
    setCalling(true);
    try {
      const { data } = await apiCall("post", "/dialpad/call", { number });
      toast.success("Call initiated!");
      setCallModal({ event_id: data.event_id });
      setCallResponse({ response: "", notes: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Call failed"); }
    finally { setCalling(false); }
  };

  const submitResponse = async () => {
    if (!callResponse.response) { toast.error("Select response"); return; }
    try {
      await apiCall("post", "/call-response", { client_id: "", call_id: callModal.event_id || "", response: callResponse.response, notes: callResponse.notes });
      toast.success("Saved");
      setCallModal(null);
      setNumber("");
    } catch { toast.error("Failed"); }
  };

  const keys = [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["*", "0", "#"]];

  return (
    <div className="max-w-sm mx-auto space-y-6" data-testid="dialpad-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Dialpad</h2>
        <p className="text-sm text-muted-foreground mt-1">Dial any number to call</p>
      </div>

      <Card className="border border-border">
        <CardContent className="p-6">
          {/* Number Display */}
          <div className="flex items-center gap-2 mb-6">
            <Input value={number} onChange={e => setNumber(e.target.value.replace(/[^0-9+*#]/g, ""))} placeholder="Enter number..." className="text-center text-2xl font-mono h-14 tracking-widest" data-testid="dialpad-input" />
            <Button variant="ghost" size="icon" className="h-14 w-14 shrink-0" onClick={backspace}><Delete className="w-5 h-5" /></Button>
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {keys.flat().map(key => (
              <Button key={key} variant="outline" className="h-14 text-xl font-medium rounded-full" onClick={() => dial(key)} data-testid={`key-${key}`}>{key}</Button>
            ))}
          </div>

          {/* Call Button */}
          <Button className="w-full h-14 text-lg rounded-full bg-green-600 hover:bg-green-700" onClick={makeCall} disabled={calling || number.length < 10} data-testid="dialpad-call-btn">
            <Phone className="w-5 h-5 mr-2" /> {calling ? "Calling..." : "Call"}
          </Button>
        </CardContent>
      </Card>

      {/* Call Response */}
      <Dialog open={!!callModal} onOpenChange={() => setCallModal(null)}>
        <DialogContent className="sm:max-w-md max-w-[95vw]">
          <DialogHeader><DialogTitle>Call Response</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-2 rounded bg-green-500/5 border border-green-500/20"><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /><p className="text-xs">Calling {number}...</p></div></div>
            <div className="grid grid-cols-2 gap-1.5">
              {["interested", "not_interested", "follow_up", "not_connected"].map(r => (
                <Button key={r} variant={callResponse.response === r ? "default" : "outline"} size="sm" onClick={() => setCallResponse({ ...callResponse, response: r })} className="capitalize text-[10px] h-8">{r.replace(/_/g, " ")}</Button>
              ))}
            </div>
            <Textarea placeholder="Notes..." value={callResponse.notes} onChange={e => setCallResponse({ ...callResponse, notes: e.target.value })} rows={2} className="text-sm" />
          </div>
          <DialogFooter className="flex-row gap-2"><Button variant="outline" size="sm" onClick={() => setCallModal(null)}>Close</Button><Button size="sm" onClick={submitResponse}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
